import { useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, useGLTF } from '@react-three/drei'
import * as THREE from 'three'

interface HelmetControlPanelProps {
  onColorChange: (color: string) => void
  onSizeChange: (size: number) => void
  onRotationChange: (rotation: { x: number; y: number; z: number }) => void
}

function MiniHelmet({ color, size, rotation }: { color: string; size: number; rotation: { x: number; y: number; z: number } }) {
  const { scene } = useGLTF('/3D/daft.glb')
  
  // Clone the scene to modify materials
  const clonedScene = scene.clone()
  
  // Make all materials chrome-like with custom color
  clonedScene.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      (child as THREE.Mesh).material = new THREE.MeshStandardMaterial({
        color: color,
        metalness: 1.0,
        roughness: 0.01,
        envMapIntensity: 2.0
      })
    }
  })
  
  return (
    <primitive object={clonedScene} scale={[size, size, size]} rotation={[rotation.x, rotation.y, rotation.z]} />
  )
}

export function HelmetControlPanel({ onColorChange, onSizeChange, onRotationChange }: HelmetControlPanelProps) {
  const [selectedColor, setSelectedColor] = useState('#1a1a1a')
  const [selectedSize, setSelectedSize] = useState(1)
  const [isOpen, setIsOpen] = useState(false)
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 })
  const [isAtTop, setIsAtTop] = useState(true)
  const [colorClickCount, setColorClickCount] = useState(0)
  const [mouseX, setMouseX] = useState(0)
  const [mouseY, setMouseY] = useState(0)

  // Handle scroll to determine if we're at hero page
  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY < 100)
      // Close interface when scrolling starts
      if (window.scrollY > 0) {
        setIsOpen(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle mouse movement to sync with hero helmet
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      // Normalize mouse position to -1 to 1 (same as hero)
      const normalizedX = (event.clientX / window.innerWidth) * 2 - 1
      const normalizedY = -(event.clientY / window.innerHeight) * 2 + 1
      
      setMouseX(normalizedX)
      setMouseY(normalizedY)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const colors = [
    '#00ffff', '#ff00ff', '#00ff00', '#ff0000', '#ffff00', '#ffffff', '#ffaa00', '#aa00ff', '#00ffaa', '#ff00aa'
  ]

  const handleRandomColor = () => {
    setColorClickCount(prev => prev + 1)
    
    if (colorClickCount + 1 === 10) {
      // 10th click - return to original chrome
      setSelectedColor('#1a1a1a')
      onColorChange('#1a1a1a')
      setColorClickCount(0)
    } else {
      // Random color selection
      const randomIndex = Math.floor(Math.random() * colors.length)
      const randomColor = colors[randomIndex]
      setSelectedColor(randomColor)
      onColorChange(randomColor)
    }
  }

  const handleSizeChange = (size: number) => {
    setSelectedSize(size)
    onSizeChange(size)
  }

  const handleRotationChange = (newRotation: { x: number; y: number; z: number }) => {
    setRotation(newRotation)
    onRotationChange(newRotation)
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      {/* Collapsed Preview */}
      {!isOpen && (
        <div
          onClick={() => {
            if (isAtTop) {
              // In hero page - open interface
              setIsOpen(true)
            } else {
              // In other sections - return to home
              const homeSection = document.getElementById('home')
              if (homeSection) {
                homeSection.scrollIntoView({ behavior: 'smooth' })
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }
          }}
          className="w-20 h-20 cursor-pointer"
          title={isAtTop ? "Open Helmet Interface" : "Return to Home"}
        >
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#7ec876] rounded-full"></div>
          </div>
        </div>
      )}

      {/* Expanded Interface */}
      {isOpen && (
        <div className="w-32 bg-gradient-to-br from-black to-gray-900 backdrop-blur-md border border-[#7ec876]/50 shadow-[0_0_20px_rgba(126,200,118,0.1)] p-3">
          {/* Title */}
          <div className="text-[#7ec876] text-xs font-mono mb-2 border-b border-[#7ec876]/50 pb-1 tracking-wider flex justify-between items-center">
            <span>SYSTEM: HELMET</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-[#7ec876] transition-colors"
            >
              ✕
            </button>
          </div>
          
          {/* 3D Preview */}
          <div className="w-full h-20 mb-3 border border-[#7ec876]/50 bg-gradient-to-br from-gray-800 to-gray-900 shadow-inner">
            <Canvas camera={{ position: [0, 0, 2], fov: 60 }}>
              <ambientLight intensity={0.5} />
              <pointLight position={[1, 1, 1]} intensity={1} color="#ffffff" />
              <pointLight position={[-1, -1, -1]} intensity={0.5} color="#00ffff" />
              <MiniHelmet color="#7ec876" size={0.5} rotation={{ x: mouseY * 0.5, y: mouseX * Math.PI * 0.5, z: 0 }} />
              <OrbitControls 
                enableZoom={false} 
                enablePan={false} 
                autoRotate 
                autoRotateSpeed={0.5}
              />
            </Canvas>
          </div>

          {/* Color Controls */}
          <div className="mb-3">
            <button
              onClick={handleRandomColor}
              className="w-full h-10 border border-[#7ec876]/50 bg-[#7ec876]/20 hover:bg-[#7ec876]/30 transition-all duration-200 rounded-md font-mono text-[#7ec876] text-sm"
            >
              TINT
            </button>
          </div>

          {/* Size Slider */}
          <div>
            <div className="text-[#7ec876] text-xs font-mono mb-2 tracking-wider">SCALE</div>
            <div className="relative">
              <input
                type="range"
                min="0.5"
                max="1.5"
                step="0.25"
                value={selectedSize}
                onChange={(e) => handleSizeChange(parseFloat(e.target.value))}
                className="w-full h-1 bg-[#7ec876]/30 rounded-lg appearance-none cursor-pointer slider accent-[#7ec876]"
              />
              <div className="flex justify-between text-xs text-[#7ec876] mt-1">
                <span>0.5x</span>
                <span>1.5x</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}